/** The three chips the old home page carried: the Hijri date, the weather where
 *  the reader is, and the next prayer. Same public sources, same caching, same
 *  fallback to Riyadh when the location cannot be resolved. */

const CACHE_TTL_MS = {
    geo: 24 * 60 * 60 * 1000,
    weather: 30 * 60 * 1000,
    prayer: 6 * 60 * 60 * 1000,
};

export const DEFAULT_LOCATION = { lat: 24.7136, lon: 46.6753, city: "Riyadh" };

const WMO_ICONS: Record<number, string> = {
    0: "fa-sun-o", 1: "fa-sun-o", 2: "fa-cloud", 3: "fa-cloud",
    45: "fa-align-justify", 48: "fa-align-justify",
    51: "fa-tint", 53: "fa-tint", 55: "fa-tint",
    61: "fa-tint", 63: "fa-tint", 65: "fa-tint",
    71: "fa-snowflake-o", 73: "fa-snowflake-o", 75: "fa-snowflake-o",
    80: "fa-tint", 81: "fa-tint", 82: "fa-tint",
    95: "fa-bolt", 96: "fa-bolt", 99: "fa-bolt",
};

const HIJRI_MONTHS: Record<string, string> = {
    "Muḥarram": "Muharram",
    "Ṣafar": "Safar",
    "Rabīʿ al-awwal": "Rabi' Al-Awwal",
    "Rabīʿ al-thānī": "Rabi' Al-Thani",
    "Jumādá al-awwal": "Jumada Al-Awwal",
    "Jumādá al-ākhirah": "Jumada Al-Thani",
    "Rajab": "Rajab",
    "Shaʿbān": "Sha'ban",
    "Ramaḍān": "Ramadan",
    "Shawwāl": "Shawwal",
    "Dhū al-Qaʿdah": "Dhu Al-Qi'dah",
    "Dhū al-Ḥijjah": "Dhu Al-Hijjah",
};

export const WEATHER_KEYS: Record<number, string> = {
    0: "weather.clear", 1: "weather.mostlyClear", 2: "weather.partlyCloudy",
    3: "weather.cloudy", 45: "weather.foggy", 48: "weather.freezingFog",
    51: "weather.lightDrizzle", 53: "weather.drizzle", 55: "weather.heavyDrizzle",
    61: "weather.lightRain", 63: "weather.rain", 65: "weather.heavyRain",
    71: "weather.lightSnow", 73: "weather.snow", 75: "weather.heavySnow",
    80: "weather.lightShowers", 81: "weather.showers", 82: "weather.stormyShowers",
    95: "weather.thunderstorm", 96: "weather.hailStorm", 99: "weather.severeStorm",
};

export const padZ = (value: number) => String(value).padStart(2, "0");
export const fmtTime = (hours: number, minutes: number) => `${padZ(hours)}:${padZ(minutes)}`;

interface Cached<T> {
    value: T;
    until: number;
}

function readCache<T>(key: string): T | null {
    try {
        const raw = window.localStorage.getItem(`promaster_home.${key}`);
        if (!raw) {
            return null;
        }
        const parsed = JSON.parse(raw) as Cached<T>;
        return parsed.until > Date.now() ? parsed.value : null;
    } catch {
        return null;
    }
}

function writeCache<T>(key: string, value: T, ttl: number) {
    try {
        window.localStorage.setItem(`promaster_home.${key}`,
                                    JSON.stringify({ value, until: Date.now() + ttl }));
    } catch {
        // a browser that refuses storage simply refetches
    }
}

async function fetchJson<T>(url: string, timeout: number): Promise<T> {
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), timeout);
    try {
        const response = await fetch(url, { signal: controller.signal });
        if (!response.ok) {
            throw new Error(String(response.status));
        }
        return (await response.json()) as T;
    } finally {
        window.clearTimeout(timer);
    }
}

export interface Location { lat: number; lon: number; city: string }

export async function resolveLocation(): Promise<Location> {
    const cached = readCache<Location>("geo");
    if (cached) {
        return cached;
    }
    try {
        const geo = await fetchJson<{ latitude?: number; longitude?: number; city?: string }>(
            "https://ipapi.co/json/", 4000);
        const location = {
            lat: geo.latitude ?? DEFAULT_LOCATION.lat,
            lon: geo.longitude ?? DEFAULT_LOCATION.lon,
            city: geo.city ?? DEFAULT_LOCATION.city,
        };
        writeCache("geo", location, CACHE_TTL_MS.geo);
        return location;
    } catch {
        return DEFAULT_LOCATION;
    }
}

export interface Weather { code: number; temp: number }

export async function fetchWeather({ lat, lon }: Location): Promise<Weather> {
    const key = `weather:${lat.toFixed(2)},${lon.toFixed(2)}`;
    const cached = readCache<Weather>(key);
    if (cached) {
        return cached;
    }
    const data = await fetchJson<{ current?: { weather_code?: number; temperature_2m?: number } }>(
        `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}`
        + "&current=temperature_2m,weather_code", 5000);
    const payload = {
        code: data.current?.weather_code ?? 0,
        temp: Math.round(data.current?.temperature_2m ?? 0),
    };
    writeCache(key, payload, CACHE_TTL_MS.weather);
    return payload;
}

export function weatherIcon(code: number): string {
    return WMO_ICONS[code] ?? "fa-sun-o";
}

export interface Hijri { day: string; month: { en: string; ar: string }; year: string }
export interface PrayerData { timings: Record<string, string>; hijri?: Hijri }

export async function fetchPrayerAndHijri({ lat, lon }: Location): Promise<PrayerData> {
    const today = new Date();
    const dateKey = `${today.getFullYear()}-${padZ(today.getMonth() + 1)}-${padZ(today.getDate())}`;
    const key = `prayer:${dateKey}:${lat.toFixed(2)},${lon.toFixed(2)}`;
    const cached = readCache<PrayerData>(key);
    if (cached) {
        return cached;
    }
    const url = `https://api.aladhan.com/v1/timings/${padZ(today.getDate())}-`
        + `${padZ(today.getMonth() + 1)}-${today.getFullYear()}`
        + `?latitude=${lat}&longitude=${lon}&method=4`;
    const data = await fetchJson<{ data: { timings: Record<string, string>; date?: { hijri?: Hijri } } }>(
        url, 6000);
    const payload: PrayerData = {
        timings: {
            Fajr: data.data.timings.Fajr ?? "",
            Dhuhr: data.data.timings.Dhuhr ?? "",
            Asr: data.data.timings.Asr ?? "",
            Maghrib: data.data.timings.Maghrib ?? "",
            Isha: data.data.timings.Isha ?? "",
        },
        hijri: data.data.date?.hijri,
    };
    writeCache(key, payload, CACHE_TTL_MS.prayer);
    return payload;
}

export function hijriLabel(hijri: Hijri | undefined, lang: string): string {
    if (!hijri?.month) {
        return "";
    }
    const month = lang.startsWith("ar")
        ? (hijri.month.ar || hijri.month.en)
        : (HIJRI_MONTHS[hijri.month.en] ?? hijri.month.en);
    return `${hijri.day} ${month} ${hijri.year}`;
}

export function nextPrayer(timings: Record<string, string>): {
    name: string; time: string; minutesAway: number;
} | null {
    const now = new Date();
    const minutesNow = now.getHours() * 60 + now.getMinutes();
    const order = ["Fajr", "Dhuhr", "Asr", "Maghrib", "Isha"];
    for (const name of order) {
        const value = (timings[name] ?? "").slice(0, 5);
        const [hours, minutes] = value.split(":").map(Number);
        if (hours === undefined || minutes === undefined || Number.isNaN(hours)) {
            continue;
        }
        const at = hours * 60 + minutes;
        if (at >= minutesNow) {
            return { name, time: value, minutesAway: at - minutesNow };
        }
    }
    const first = (timings.Fajr ?? "").slice(0, 5);
    const [hours, minutes] = first.split(":").map(Number);
    if (hours === undefined || minutes === undefined || Number.isNaN(hours)) {
        return null;
    }
    return { name: "Fajr", time: first,
             minutesAway: 24 * 60 - minutesNow + hours * 60 + minutes };
}
